import { Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import NavigationLink from 'components/Navigation/NavigationLink';
import LanguageSelector from 'components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import { URLS } from 'config/urls';
import { Button } from 'components/ui/button.tsx';
import { Separator } from 'components/ui/separator';
import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/20/solid';
import { FlightTakeoffIcon } from '@/assets/icons/FlightTakeoffIcon';
import { cn } from 'utils/utils';

interface MobileNavigationProps {
  openIcon: ReactNode;
  closeIcon: ReactNode;
  handleLogout: () => Promise<void>;
}

const MENU_PORTAL_ID = 'headlessui-portal-root';

const MobileNavigation = ({ openIcon, closeIcon, handleLogout }: MobileNavigationProps) => {
  const [mounted, setMounted] = useState(false);
  const [animate, setAnimate] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const languageSelectorRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const openMenu = () => {
    setMounted(true);

    requestAnimationFrame(() => {
      setAnimate(true);
    });
  };

  const closeMenu = () => {
    setAnimate(false);
    setTimeout(() => setMounted(false), 300); // match duration-300
  };

  useEffect(() => {
    const handleOutsideMenu = (event: MouseEvent) => {
      const langEl = document.getElementById(MENU_PORTAL_ID);
      if (
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        !languageSelectorRef.current?.contains(event.target as Node) &&
        !langEl?.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      const langEl = document.getElementById(MENU_PORTAL_ID);
      if (event.key === 'Escape' && langEl === null) closeMenu();
    };

    document.addEventListener('keyup', handleEscapeKey);
    document.addEventListener('click', handleOutsideMenu, true);

    return () => {
      document.removeEventListener('keyup', handleEscapeKey);
      document.removeEventListener('click', handleOutsideMenu, true);
    };
  }, []);

  useEffect(() => {
    if (mounted) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mounted]);

  return (
    <Fragment>
      <div className='flex items-center gap-4 md:hidden'>
        <div ref={languageSelectorRef} className='flex h-6 items-center'>
          <LanguageSelector
            mobileStyle='text-white'
            chevronClassName='fill-white text-white group-data-[popup-open]:fill-white group-data-[popup-open]:text-white'
            hoverStyle='h-6 bg-transparent p-0 hover:bg-transparent data-[popup-open]:bg-transparent'
            className='!text-white'
          />
        </div>
        <Button variant='ghost' className='relative size-6 p-0' onClick={openMenu} aria-label='Open navigation'>
          {openIcon}
        </Button>
      </div>

      {mounted && (
        <Fragment>
          <div
            className={`
              fixed inset-0 bg-grey800 transition-opacity duration-300 z-1
              ${animate ? 'opacity-50' : 'opacity-0'}
            `}
          />

          <nav
            ref={ref}
            className={cn(
              'fixed top-0 right-0 z-2 h-screen w-2/3 bg-white shadow-2xs transition-transform duration-300 ease-out',
              animate ? 'translate-x-0' : 'translate-x-full'
            )}
          >
            <header className='relative flex h-[65px] w-full justify-end px-6 pt-3'>
              <Button
                variant='ghost'
                className='relative size-8 rounded-full bg-brand-brown p-1 hover:bg-brand-brown hover:text-white'
                onClick={closeMenu}
                aria-label='Close navigation'
              >
                {closeIcon}
              </Button>
            </header>

            <div className='flex flex-col items-stretch gap-4 px-6 pt-0'>
              <NavigationLink
                className='flex min-h-12 items-center gap-3 text-content-primary max-md:!text-content-primary'
                href={URLS.FLIGHTS}
              >
                <FlightTakeoffIcon className='size-6 text-content-primary' />
                {t('navigation.flights')}
              </NavigationLink>
              <Separator className='-my-2' />
              <button
                onClick={handleLogout}
                className='flex min-h-12 cursor-pointer items-center gap-3 text-left font-[MuseoSans900] tracking-[1.5px] text-content-primary normal-case'
              >
                <ArrowRightStartOnRectangleIcon className='size-6 text-content-primary' />
                {t('navigation.logout')}
              </button>
            </div>
          </nav>
        </Fragment>
      )}
    </Fragment>
  );
};

export default MobileNavigation;
